import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, FolderOpen, Settings, CheckCircle2, Smartphone } from "lucide-react";

const steps = [
  {
    icon: Settings,
    title: "Enable Unknown Sources",
    desc: "Go to Settings → Apps → Special access → Install unknown apps, then allow your browser or file manager.",
  },
  {
    icon: FolderOpen,
    title: "Open the downloaded APK",
    desc: "Pull down the notification shade or open Files, then tap the .apk you just downloaded.",
  },
  {
    icon: ShieldCheck,
    title: "Confirm the install",
    desc: "Tap Install. If a Play Protect warning appears, choose 'Install anyway' — the APK is virus-checked by crazyOne.",
  },
  {
    icon: CheckCircle2,
    title: "Launch & enjoy",
    desc: "Once installed, tap Open. Return here anytime to grab updates in one tap.",
  },
];

export const InstallGuide = ({ open, appName, onClose }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="install-guide-overlay"
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="install-guide-card"
            className="co-scanline relative w-full max-w-lg overflow-hidden rounded-t-2xl border border-[#1E2433] bg-[#10131B] p-6 sm:rounded-2xl"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="mb-5 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="co-glow-cyan flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 text-[#05060a]">
                  <Smartphone className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Download started</p>
                  <h2 className="font-display text-xl font-bold text-slate-100">How to install {appName}</h2>
                </div>
              </div>
              <button
                data-testid="install-guide-close"
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-[#1E2433] hover:text-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-3 rounded-xl border border-[#1E2433] bg-[#0b0d13]/60 p-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-emerald-400">
                    <s.icon className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">
                      <span className="font-mono text-emerald-400">{i + 1}.</span> {s.title}
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid="install-guide-done"
              onClick={onClose}
              className="co-btn-primary mt-5 w-full rounded-lg py-2.5 text-sm"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
